<template>
  <v-dialog
    transition="custom-dialog-transition"
    ref="dialog"
    v-model="isOpen"
  >
    <div class="card-overlay" @click="onOverlayClick" />
    <v-card style="width: 600px" class="" v-if="isOpen">
      <v-card-title class="pb-0 flex justify-space-between align-center">
        <h4>
          {{ $t("heading.onboarding.transferDomain.title") }}
        </h4>
        <v-btn x-small icon :disabled="submitting" @click="closeModal" class="close-model">
          <v-icon>$close</v-icon>
        </v-btn>
      </v-card-title>

      <template v-if="selectedService.plan_settings.domain_transfer_enabled">
        <v-card-text class="px-10 pt-8">
          <div v-if="localErrorMessage" class="mb-6 alert-box alert">
            <span class="font-weight-600 mb-0">{{ localErrorMessage }}</span>
          </div>

          <h6 class="p-2 font-weight-light">
            <i18next
              :translation="$t('heading.onboarding.transferDomain.subtitle')"
            >
              <template #domain><span class="font-weight-600">{{ domain.domain }}</span></template>
            </i18next>
          </h6>

          <div class="p-2 mt-2 text-justify text--darken1">
            <p class="mb-4 text--darken1">
              <i18next
                :translation="$t('heading.onboarding.transferDomain.message.possible.title')"
              >
                <template #domain
                  ><span class="font-weight-800">{{
                    domain.domain
                  }}</span></template
                >
              </i18next>
            </p>

            <p class="mb-3 text--darken1">
              {{ $t("heading.onboarding.transferDomain.message.possible.stepsTitle") }}
            </p>

            <ol class="pl-4">
              <li class="mb-1 text--darken1">
                <i18next
                  :translation="$t('heading.onboarding.transferDomain.message.possible.step1')"
                >
                  <template #domain
                    ><span class="font-weight-800">{{
                      domain.domain
                    }}</span></template
                  >
                </i18next>
              </li>
              <li class="mb-1 text--darken1">
                <i18next
                  :translation="$t('heading.onboarding.transferDomain.message.possible.step2WithHost')"
                >
                  <template #domain><span class="font-weight-800">{{ effectiveDnsRecord.name }}</span></template>
                  <template #recordHost><span class="font-weight-800">@</span></template>
                  <template #recordType
                    ><span class="font-weight-800">{{
                      effectiveDnsRecord.type
                    }}</span></template
                  >
                </i18next>
              </li>
              <li class="mb-1 text--darken1">
                {{ $t("heading.onboarding.transferDomain.message.possible.step3") }}
              </li>
              <li class="text--darken1">
                {{ $t("heading.onboarding.transferDomain.message.possible.step4") }}
              </li>
            </ol>
          </div>

          <long-value-box
            class="mt-4"
            v-if="effectiveDnsRecord && effectiveDnsRecord.content"
            :value="effectiveDnsRecord.content"
            :can-expand="false"
          />
        </v-card-text>
      </template>
      <template v-else>
        <v-card-text class="px-10 pt-8">
          <h6 class="p-2 font-weight-light">
            <i18next
              :translation="$t('heading.onboarding.transferDomain.subtitle')"
            >
              <template #domain><span class="font-weight-600">{{ domain.domain }}</span></template>
            </i18next>
          </h6>
          <p class="p-3 mt-2">
            {{ $t('heading.onboarding.transferDomain.message.impossible') }}
          </p>
        </v-card-text>
      </template>

      <v-card-actions class="d-flex flex-column pb-6 px-10 pt-2 mt-4">
        <template v-if="selectedService.plan_settings.domain_transfer_enabled">
          <v-btn
            x-large
            elevation="0"
            color="primary"
            block
            :disabled="submitting"
            @click="handleTransferDomain"
            :loading="submitting"
          >{{ $t('button.transferNow') }}
          </v-btn>
          <v-btn
            x-large
            elevation="0"
            class="mx-0 mt-2"
            color="gray"
            text
            block
            :disabled="submitting"
            @click="closeModal"
          >
            <span class="p-1 font-weight-light gray--text text--darken-1">
              {{ $t("button.back") }}
            </span>
          </v-btn>
        </template>
        <template v-else>
          <v-btn
            x-large
            elevation="0"
            color="primary"
            block
            :disabled="submitting"
            @click="closeModal"
          >{{ $t('button.back') }}
          </v-btn>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Api from "@/apis/Api";
import LongValueBox from "@/components/common/LongValueBox.vue";

export default {
  name: "TransferDomainModal",
  components: { LongValueBox },
  props: {
    isOpen: Boolean,
    selectedService: Object,
    domain: Object,
    dnsRecord: Object,
  },
  data() {
    return {
      submitting: false,
      localErrorMessage: "",
      localDnsRecord: null,
    }
  },
  computed: {
    effectiveDnsRecord() {
      return this.localDnsRecord || this.dnsRecord;
    },
  },
  mounted() {
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
  },
  methods: {
    closeModal() {
      if (!this.submitting) {
        this.$emit('removeModal');
      }
    },
    onOverlayClick() {
      if (!this.submitting && this.isOpen) {
        this.$emit('removeModal');
      }
    },
    handleBeforeUnload(e) {
      if (!this.submitting) {
        return;
      }
      const message = 'Transfer is in progress. Leaving the page may cause the process to fail.';
      (e || window.event).returnValue = message;
      return message;
    },
    async handleTransferDomain() {
      if (this.submitting) return;
      this.submitting = true;
      this.localErrorMessage = "";
      try {
        const response = await Api.get(
          `/services/${this.selectedService.id}/domains/verify?domain=${this.domain.domain}`
        );
        const result = response.data;

        if (result?.verification_required) {
          this.localDnsRecord = result.verification_record;
          this.$emit("dns-record-updated", result.verification_record);
          this.localErrorMessage = this.$t(
            "heading.onboarding.transferDomain.message.verificationRequired"
          );
          return;
        }

        this.$emit('transfer-domain', this.domain);
      } catch (error) {
        this.localErrorMessage = Api.getErrorMessage(error);
        await this.$store.dispatch("addAlert", {
          success: false,
          errorMessage: Api.getErrorMessage(error),
        });
      } finally {
        this.submitting = false;
      }
    }
  },
  watch: {
    isOpen: function (value) {
      if (value) {
        this.localErrorMessage = "";
        this.localDnsRecord = this.dnsRecord || null;
        this.$store.dispatch("lockBodyScroll");
      } else {
        this.submitting = false;
        this.$store.dispatch("unlockBodyScroll");
      }
    },
  },
}
</script>

<style scoped lang="scss">
.alert-box {
  width: 100%;
  min-width: 100%;
  padding: 16px;
  text-align: center;
  border-radius: 8px;

  &.alert {
    background: var(--v-error-lighten5);
    color: var(--v-error-base);
  }
}
</style>